import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { Phone, WhatsappLogo, MapPin, Star, ArrowRight, Storefront, Clock } from "@phosphor-icons/react";
import { cloudy, cloudyLogo } from "../lib/cloudy";
import { getOpenStatus } from "../lib/openStatus";
import ShareButton from "./ShareButton";

// Deterministic palette for branded placeholder (based on business id / name)
const PLACEHOLDER_GRADIENTS = [
  ["#134E34", "#2A7A57"], // vosges green
  ["#C8795A", "#E8B896"], // terracotta
  ["#3D5A3F", "#7A9A7E"], // alsace forest
  ["#A05440", "#D6907A"], // burnt clay
  ["#1F4A5C", "#4A8095"], // slate blue
  ["#7E6741", "#B8A179"], // sand
];

function pickGradient(b) {
  const h = (b.id || b.name || "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return PLACEHOLDER_GRADIENTS[h % PLACEHOLDER_GRADIENTS.length];
}

function initials(name) {
  if (!name) return "68";
  const parts = name.replace(/[^A-Za-zÀ-ÿ\s]/g, " ").trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase().slice(0, 2);
}

export default function BusinessCard({ b }) {
  const { t } = useI18n();
  const isPremium = b.premium && (b.premium.tier === "premium" || b.premium.tier === "premium_ai");
  const hasPhoto = b.photos && b.photos.length > 0;
  const [c1, c2] = pickGradient(b);
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/entreprise/${b.slug}` : `/entreprise/${b.slug}`;
  const openStatus = getOpenStatus(b.hours);
  const openBadge = openStatus?.state === "open"
    ? { label: "Ouvert", cls: "bg-primary text-primary-foreground" }
    : openStatus?.state === "lunch_break"
      ? { label: `Ouvre à ${openStatus.reopensAt}`, cls: "bg-secondary/95 text-secondary-foreground" }
      : openStatus?.state === "closed_today_opens_later"
        ? { label: `Ouvre à ${openStatus.reopensAt}`, cls: "bg-secondary/95 text-secondary-foreground" }
        : null;
  return (
    <div className="relative lift-card group bg-card border border-border rounded-sm overflow-hidden" data-testid={`business-card-${b.slug}`}>
      <Link
        to={`/entreprise/${b.slug}`}
        className="block"
      >
      <div className="relative aspect-[4/3] overflow-hidden">
        {hasPhoto ? (
          <img
            src={cloudy(b.photos[0], { w: 640, q: "auto:eco" })}
            alt={b.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
          />
        ) : (
          <div
            className="absolute inset-0 grid place-items-center"
            style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}
            aria-hidden
          >
            {b.logo ? (
              <img src={cloudyLogo(b.logo)} alt="" className="w-2/3 h-2/3 object-contain" loading="lazy" />
            ) : (
              <div className="flex flex-col items-center text-white/95">
                <Storefront size={44} weight="duotone" className="mb-2 opacity-80" />
                <div className="font-display text-4xl font-medium tracking-tight drop-shadow-sm">{initials(b.name)}</div>
              </div>
            )}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.18), transparent 60%)" }} />
          </div>
        )}
        {isPremium && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-sm" data-testid="premium-badge">
            ★ {t.directory.premium_badge}
          </span>
        )}
        {(b.rating_count > 0 || b.reviews_count > 0) && (b.rating_avg != null || b.avg_rating != null || b.rating != null) && (
          <span
            className="absolute top-3 left-3 inline-flex items-center gap-1 bg-card text-foreground text-xs font-semibold px-2 py-1 rounded-sm border border-border shadow-sm"
            data-testid={`card-rating-${b.slug || b.id}`}
            style={{ top: isPremium ? "2.75rem" : "0.75rem" }}
          >
            <Star size={12} weight="fill" className="text-secondary" /> {Number(b.rating_avg ?? b.avg_rating ?? b.rating).toFixed(1)}
          </span>
        )}
        {openBadge && (
          <span
            className={`absolute bottom-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-sm shadow-sm ${openBadge.cls}`}
            data-testid={`open-status-${openStatus.state}`}
          >
            <Clock size={11} weight="fill"/> {openBadge.label}
          </span>
        )}
        {b.dnc && (
          <span
            className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-sm bg-muted text-muted-foreground border border-border"
            data-testid={`dnc-badge-${b.slug || b.id}`}
            title="Ce commerce nous a demandé de ne plus être contacté pour du démarchage."
          >
            Fermé démarchage
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1.5">{b.category_label || b.category || "Commerce"}</div>
        <h3 className="font-display text-lg font-semibold leading-snug mb-2 group-hover:text-primary transition-colors">{b.name}</h3>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
          <MapPin size={14} weight="duotone" /> {b.city || "Haut-Rhin"} {b.postal_code ? `· ${b.postal_code}` : ""}
        </div>
        {isPremium ? (
          <div className="flex flex-wrap gap-2">
            {b.phone && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary border border-primary/30 px-2 py-1 rounded-sm">
                <Phone size={12} weight="fill" /> Appel direct
              </span>
            )}
            {b.whatsapp && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary border border-secondary/30 px-2 py-1 rounded-sm">
                <WhatsappLogo size={12} weight="fill" /> WhatsApp
              </span>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
            {t.directory.view} <ArrowRight size={12} />
          </span>
        )}
      </div>
      </Link>
      {/* Share button — positioned overlay so click doesn't trigger card navigation */}
      <div className="absolute top-2 right-2 z-10 bg-white/85 backdrop-blur rounded-sm shadow-sm" onClick={(e) => e.stopPropagation()}>
        <ShareButton compact url={shareUrl} title={`${b.name} — ${b.city}`} text={`${b.name}, ${b.category || ""} à ${b.city}`}/>
      </div>
    </div>
  );
}
